// A REVISTA do app Luísa (28/09/2026): o escritório cola o link da Publitas
// da Tupperware e o CRM copia páginas, produtos e preços para o app mostrar na
// vitrine de cada consultora. Backend: server/routes/vitrine.cjs (importador
// lib/vitrine.cjs, o mesmo da matriz). Uma edição ativa por vez: importar a
// nova tira a anterior de cartaz, e produto que saiu da edição é desativado,
// nunca apagado.
import { useEffect, useState } from 'react'
import { FiBookOpen, FiDownloadCloud, FiCheckCircle, FiAlertCircle, FiLoader } from 'react-icons/fi'
import { useApp, useEnviar, Cabeca, Carregando, Falha, Selo, num, quando } from './comum'

interface Vitrine {
  id: number
  url: string
  titulo: string | null
  num_paginas: number
  status: 'importando' | 'pronta' | 'falha'
  erro: string | null
  ativa: number
  importada_em: string | null
  progresso_feito: number
  progresso_total: number
  progresso_etapa: string | null
  produtos?: number
  balanco: {
    paginas: number; produtos: number; novos: number; atualizados: number
    precos_mudados: number; desativados: number; sem_cartao: number
  } | null
}

export function Revista({ gere }: { gere: boolean }) {
  const { dados, erro, carregando, recarregar } = useApp<{ vitrines: Vitrine[] }>('/api/loja/admin/vitrines')
  const enviar = useEnviar()
  const [link, setLink] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [erroLink, setErroLink] = useState<string | null>(null)
  const vitrines = dados?.vitrines || []
  const emCurso = vitrines.some(v => v.status === 'importando')

  // Enquanto uma edição está sendo copiada, a tela se repõe sozinha: o balanço
  // só existe no fim, e sem o laço quem clicou ficaria olhando "começando".
  useEffect(() => {
    if (!emCurso) return
    const t = setTimeout(recarregar, 1500)
    return () => clearTimeout(t)
  }, [emCurso, dados, recarregar])

  const importar = async () => {
    setErroLink(null); setEnviando(true)
    const r = await enviar('/api/loja/admin/vitrine/importar', { url: link.trim() })
    setEnviando(false)
    if (!r.ok) { setErroLink(r.erro || 'Não consegui começar a importação.'); return }
    setLink('')
    recarregar()
  }

  if (!dados && carregando) return <Carregando />
  if (erro && !dados) return <Falha erro={erro} tentar={recarregar} />
  const ativa = vitrines.find(v => v.ativa === 1)

  return (
    <div className="h-full overflow-y-auto p-4 sm:p-5 space-y-4">
      <Cabeca titulo="Revista" sub="A vitrine que as consultoras mostram no app Luísa — copiada do link da Publitas da Tupperware"
        carregando={carregando} recarregar={recarregar}
        chips={ativa ? <Selo tom="bom">no ar: {ativa.titulo || `edição ${ativa.id}`}</Selo> : <Selo tom="alerta">nenhuma revista no ar</Selo>} />

      {gere && (
        <section className="glass-panel rounded-2xl p-4 space-y-2">
          <label htmlFor="link-revista" className="text-[13px] font-semibold flex items-center gap-2">
            <FiBookOpen className="w-4 h-4" /> Trocar a revista
          </label>
          <p className="text-[12px] text-muted-foreground">
            Cole o link da revista na Publitas (começa com https://view.publitas.com/…). A cópia leva de segundos a
            alguns minutos; a revista nova entra no ar sozinha quando terminar.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input id="link-revista" value={link} onChange={e => setLink(e.target.value)} disabled={enviando || emCurso}
              placeholder="https://view.publitas.com/…" inputMode="url"
              className="flex-1 min-w-0 rounded-xl border bg-background px-3 py-2 text-[13px]" />
            <button onClick={importar} disabled={!link.trim() || enviando || emCurso}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground px-4 py-2 text-[13px] font-medium disabled:opacity-50">
              {enviando ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiDownloadCloud className="w-4 h-4" />}
              Importar
            </button>
          </div>
          {erroLink && <p className="text-[12px] text-red-600">{erroLink}</p>}
        </section>
      )}

      <section className="space-y-2">
        {vitrines.length === 0 && <p className="text-[13px] text-muted-foreground">Nenhuma revista importada ainda.</p>}
        {vitrines.map(v => (
          <article key={v.id} className="glass-panel rounded-2xl p-4 space-y-1.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold break-words">{v.titulo || `Edição ${v.id}`}</p>
                <a href={v.url} target="_blank" rel="noreferrer" className="text-[11.5px] text-muted-foreground break-all hover:underline">{v.url}</a>
              </div>
              {v.status === 'importando' && <Selo tom="info">copiando</Selo>}
              {v.status === 'falha' && <Selo tom="ruim">falhou</Selo>}
              {v.status === 'pronta' && (v.ativa === 1 ? <Selo tom="bom">no ar</Selo> : <Selo>fora do ar</Selo>)}
            </div>
            {v.status === 'importando' && (
              <p className="text-[12px] text-muted-foreground flex items-center gap-2">
                <FiLoader className="w-3.5 h-3.5 animate-spin shrink-0" />
                {v.progresso_etapa || 'começando'}{v.progresso_total > 0 ? ` · ${v.progresso_feito}/${v.progresso_total}` : ''}
              </p>
            )}
            {v.status === 'falha' && v.erro && (
              <p className="text-[12px] text-red-600 flex items-start gap-2"><FiAlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{v.erro}</p>
            )}
            {v.status === 'pronta' && (
              <p className="text-[12px] text-muted-foreground flex items-start gap-2">
                <FiCheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  {num(v.num_paginas)} páginas · {num(v.produtos ?? 0)} produtos
                  {v.balanco ? ` · ${num(v.balanco.novos)} novos, ${num(v.balanco.precos_mudados)} com preço mudado, ${num(v.balanco.desativados)} saíram` : ''}
                  {v.importada_em ? ` · ${quando(v.importada_em)}` : ''}
                </span>
              </p>
            )}
          </article>
        ))}
      </section>
    </div>
  )
}
